import { gql } from "@apollo/client";
import { apolloCore } from "lib/apollo/apollo";

import {
  dashboardItemOnProfileGqlFragment,
  toDashboardOnProfileType,
  DashboardOnProfileType,
} from "gui/profile-shared/dashboards/dashboardItemOnProfile";
import {
  queryItemOnProfileGqlFragment,
  toQueryOnProfileType,
  QueryOnProfileType,
} from "gui/profile-shared/queries/queryItemOnProfile";

import {
  StaticTeamProfileDataDocument,
  StaticTeamProfileDataQuery,
  StaticTeamProfileDataQueryVariables,
} from "lib/types/graphql";

export default async function fetchStaticTeamProfileData(
  handle: string
): Promise<StaticTeamProfileData | undefined> {
  const res = await apolloCore.query<
    StaticTeamProfileDataQuery,
    StaticTeamProfileDataQueryVariables
  >({
    query: StaticTeamProfileDataDocument,
    variables: { handle },
    fetchPolicy: "no-cache",
  });

  return res.data.teams.map(toStaticTeamProfileData)[0];
}

gql`
  query StaticTeamProfileData($handle: String!) {
    teams(where: { handle: { _eq: $handle } }) {
      id
      name
      handle
      profile_image_url
      bio
      twitter_handle
      telegram_handle
      discord_url
      received_stars {
        sum
      }
      conditional_public_attributes {
        service_tier {
          name
        }
      }
      dashboards(
        where: { is_archived: { _eq: false } }
        limit: 20
        order_by: [
          { dashboard_favorite_count_all: { favorite_count: desc_nulls_last } }
          { id: asc }
        ]
      ) {
        ...DashboardItemOnProfile
      }
      dashboards_aggregate(where: { is_archived: { _eq: false } }) {
        aggregate {
          count
        }
      }

      queries(
        where: { is_temp: { _eq: false }, is_archived: { _eq: false } }
        limit: 20
        order_by: [
          { query_favorite_count_all: { favorite_count: desc_nulls_last } }
          { id: asc }
        ]
      ) {
        ...QueryItemOnProfile
      }
      queries_aggregate(
        where: { is_temp: { _eq: false }, is_archived: { _eq: false } }
      ) {
        aggregate {
          count
        }
      }

      memberships {
        id
        user {
          id
          name
          profile_image_url
        }
      }
    }
  }

  ${dashboardItemOnProfileGqlFragment}
  ${queryItemOnProfileGqlFragment}
`;

export interface StaticTeamProfileData {
  id: number;
  name: string;
  handle: string;
  profile_image_url: string;
  bio: string;
  twitter_handle: string;
  telegram_handle: string;
  discord_url: string;
  received_stars: number;
  serviceTierName: string | null;

  dashboards: {
    list: DashboardOnProfileType[];
    total_count: number;
  };

  queries: {
    list: QueryOnProfileType[];
    total_count: number;
  };

  members: Member[];
}

function toStaticTeamProfileData(
  team: StaticTeamProfileDataQuery["teams"][0]
): StaticTeamProfileData {
  const {
    id,
    name,
    handle,
    profile_image_url = null,
    bio,
    twitter_handle,
    telegram_handle,
    discord_url,
    received_stars,
    conditional_public_attributes,
  } = team;
  return {
    id,
    name,
    handle,
    profile_image_url: profile_image_url ?? "",
    bio: bio ?? "",
    twitter_handle: twitter_handle ?? "",
    telegram_handle: telegram_handle ?? "",
    discord_url: discord_url ?? "",
    received_stars: received_stars?.sum ?? 0,
    serviceTierName: conditional_public_attributes?.service_tier?.name ?? null,

    dashboards: {
      list: team.dashboards.map(toDashboardOnProfileType),
      total_count: team.dashboards_aggregate?.aggregate?.count ?? 0,
    },

    queries: {
      list: team.queries.map(toQueryOnProfileType),
      total_count: team.queries_aggregate?.aggregate?.count ?? 0,
    },

    members: toMemberList(team.memberships),
  };
}

export interface Member {
  name: string;
  profile_image_url?: string | null;
}

function toMemberList(
  list: StaticTeamProfileDataQuery["teams"][0]["memberships"] | undefined
): Member[] {
  if (!list) {
    return [];
  }
  const users: Member[] = list.reduce<Member[]>((final, current) => {
    if (current.user === null || current.user === undefined) return final;
    return final.concat([current.user]);
  }, []);

  return users ?? [];
}
